/**
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 **/
import React, { ReactNode, ReactElement } from 'react';

import semverSatisfies from 'semver/functions/satisfies';
import WebExtensionContext, { State } from './context';
import { ActionTypes, Action } from './actions';

import { ExtensionStatus } from '../../utils/constants';
import { areCookiesEnabled } from '../../utils/cookies';

const ExtensionKey = 'webExtensionVersion';

type Props = {
	children: ReactNode;
};

function webExtensionReducer(state: State, action: Action): State {
	switch (action.type) {
		case ActionTypes.SetRequirements: {
			if (state.installedVersion) {
				const satisfies = semverSatisfies(state.installedVersion, action.data.minVersion);
				const status = satisfies || state.useDev ? ExtensionStatus.Installed : ExtensionStatus.Outdated;
				return { ...state, minVersion: action.data.minVersion, status };
			}
			return { ...state, minVersion: action.data.minVersion };
		}
		case ActionTypes.ExtensionChecked: {
			if (state.minVersion) {
				const satisfies = semverSatisfies(action.data.detail.version, state.minVersion);
				const status = satisfies || state.useDev ? ExtensionStatus.Installed : ExtensionStatus.Outdated;
				return { ...state, status, installedVersion: action.data.detail.version };
			}
			return { ...state, installedVersion: action.data.detail.version };
		}
		case ActionTypes.CheckTimedOut: {
			return { ...state, status: ExtensionStatus.NotInstalled };
		}
		case ActionTypes.ExtensionUpdated: {
			return { ...state, status: ExtensionStatus.Installed };
		}
		case ActionTypes.ExtensionUninstalled: {
			return { ...state, status: ExtensionStatus.NotInstalled };
		}
		default: {
			throw new Error(`Unhandled action type: ${action}`);
		}
	}
}
let version;
if (areCookiesEnabled()) {
	version = localStorage.getItem(ExtensionKey);
}
const initialState = {
	status: ExtensionStatus.Pending,
	useBeta: false,
	useDev: false,
};
if (version) {
	initialState.useBeta = version === 'beta';
	initialState.useDev = version === 'dev';
}

const ExtensionProvider = ({ children }: Props): ReactElement => {
	const [state, dispatch] = React.useReducer(webExtensionReducer, initialState);
	const context = { state, dispatch };

	return <WebExtensionContext.Provider value={context}>{children}</WebExtensionContext.Provider>;
};

export default ExtensionProvider;
